
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { UserMembership } from "./user-membership"
import { UserExercisePlans } from "./user-exercise-plans"
import { UserChat } from "./user-chat"
import { UserAppointments } from "./user-appointments"
import { UserNotifications } from "./user-notifications"
import { CreditCard, Dumbbell, MessageSquare, Calendar, Bell, X } from "lucide-react"
import { useMobile } from "@/contexts/mobile-context"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"

export function UserView() {
  const { t } = useTranslation()
  const [activeView, setActiveView] = useState("membership")
  const { isMobile, isSidebarOpen, closeSidebar } = useMobile()

  const navItems = [
    {
      title: t("userView.tabs.membership"),
      href: "membership",
      icon: CreditCard,
    },
    {
      title: t("userView.tabs.exercise"),
      href: "exercise",
      icon: Dumbbell,
    },
    {
      title: t("userView.tabs.appointments"),
      href: "appointments",
      icon: Calendar,
    },
    {
      title: t("userView.tabs.chat"),
      href: "chat",
      icon: MessageSquare,
    },
    {
      title: t("userView.tabs.notifications"),
      href: "notifications",
      icon: Bell,
    },
  ]

  const handleNavigation = (href: string) => {
    setActiveView(href)
    if (isMobile) {
      closeSidebar()
    }
  }

  const renderContent = () => {
    switch (activeView) {
      case "membership":
        return <UserMembership />
      case "exercise":
        return <UserExercisePlans />
      case "appointments":
        return <UserAppointments />
      case "chat":
        return <UserChat />
      case "notifications":
        return <UserNotifications />
      default:
        return <UserMembership />
    }
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Mobile sidebar */}
      {isMobile && isSidebarOpen && (
        <div className="fixed inset-y-0 left-0 z-50 w-64 bg-background border-r">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="font-bold text-xl">{t("userView.title")}</div>
              <Button variant="ghost" size="icon" onClick={closeSidebar}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <nav className="grid gap-1 p-2">
                {navItems.map((item) => (
                  <Button
                    key={item.href}
                    variant={activeView === item.href ? "secondary" : "ghost"}
                    className="justify-start"
                    onClick={() => handleNavigation(item.href)}
                  >
                    <item.icon className="mr-2 h-4 w-4" />
                    {item.title}
                  </Button>
                ))}
              </nav>
            </ScrollArea>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      {!isMobile && (
        <div className="shrink-0 w-64 border-r bg-background">
          <div className="flex h-full flex-col">
            <div className="flex h-14 items-center border-b px-4">
              <div className="font-bold text-xl">{t("userView.title")}</div>
            </div>
            <ScrollArea className="flex-1">
              <nav className="grid gap-1 p-2">
                {navItems.map((item) => (
                  <Button
                    key={item.href}
                    variant={activeView === item.href ? "secondary" : "ghost"}
                    className="justify-start"
                    onClick={() => handleNavigation(item.href)}
                  >
                    <item.icon className="mr-2 h-4 w-4" />
                    {item.title}
                  </Button>
                ))}
              </nav>
            </ScrollArea>
          </div>
        </div>
      )}

      <div className="flex flex-col flex-1 overflow-hidden">
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="container mx-auto max-w-6xl">
            <div className="hidden md:block mb-6">
              <h1 className="text-3xl font-bold">{t("userView.welcome")}</h1>
              <p className="text-muted-foreground">{t("userView.welcomeMessage")}</p>
            </div>
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  )
}
